import { PiralPlugin } from "piral-core";
import { account, client } from "../adal";
import { AdalClient } from "@cannon-design/piral-adal";
import { useTheme } from "@mui/material";
import { useFliptBoolean, useFliptVariant, useFliptSelector } from '@flipt-io/flipt-client-react';
import type { FliptClient, ClientOptions } from '@flipt-io/flipt-client-js';


import 'piral-core';
interface CDHelperConfig {

}
type Mode = 'light' | 'dark' | 'system';
declare module 'piral-core' {
    interface PiralCustomState {
        theme?: 'light' | 'dark' | 'system';
    }
}
export interface PiletCDHelperApi {

    auth: {
        /**
         * Retrieves the current user's account information from ADAL.
         * @returns The user's account object, or null if no user is logged in.
         */
        getAccount(): any;
        /**
         * Retrieves a valid access token for the current user.
         * @returns A promise resolving with the user's access token.
         */
        getToken(): Promise<string>;
        /**
         * The underlying ADAL client instance used for authentication interactions.
         */
        client: AdalClient;
    },
    theme: {
        /**
         * Hook for accessing MUI's current theme object within a component.
         */
        useTheme();
        /**
         * Reads the current theme state ('system', 'light', or 'dark').
         * @returns The active theme mode from the Piral store.
         */
        getTheme();
        /**
         * Sets a new theme by overriding the Piral state.
         * @param theme The new theme object or definition.
         */
        setTheme(theme: any);
        /**
         * Notifies all subscribed callbacks (via events) to update the theme.
         * @param cb Callback to be fired when the theme changes.
         */
        changeTheme(cb: () => void): void;
        /**
         * Updates the theme mode in the Piral store ('light', 'dark', or 'system').
         * @param mode The new mode to set.
         */
        setMode: (mode: Mode | null) => void;
    },
    featureFlag: {
        /**
         * Checks a boolean feature flag.
         * @param flagKey The key/name of the Flipt flag.
         * @param fallback The default value if the flag cannot be retrieved.
         * @param entityId The entity ID for the flag evaluation.
         * @param context Optional evaluation context values.
         * @returns True or false, depending on the feature flag's result or fallback.
         */
        useFeatureBoolean: (flagKey: string, fallback: boolean, entityId: string, context?: Record<string, string>) => boolean;

        /**
         * Checks a variant-based feature flag.
         * @param flagKey The key/name of the Flipt flag.
         * @param fallback The default variant if the flag cannot be retrieved.
         * @param entityId The entity ID for the flag evaluation.
         * @param context Optional evaluation context values.
         * @returns The resulting variant string or fallback.
         */
        useFeatureVariant: (flagKey: string, fallback: string, entityId: string, context?: Record<string, string>) => string;

        /**
         * A hook for custom Flipt usage patterns.
         * @param selector A function receiving the Flipt client, loading state, and error.
         * @returns Whatever is returned by the selector function.
         */
        useFeatureSelector: <T>(selector: (client: FliptClient | null, isLoading: boolean, error: Error | null) => T) => T

    },
    analytics: {
        /**
         * Sends a Google Analytics event.
         * @param eventName Name of the event
         * @param eventParams Additional event parameters
         */
        trackEvent(eventName: string, eventParams?: Record<string, any>): void;
    };
};



export function createCDHelperApi(config: CDHelperConfig): PiralPlugin<PiletCDHelperApi> {
    let theme = 'light'
    let events = [];
    return (context) => ({
        auth: {
            client,
            getAccount() {
                const user = account();

                return user;
            },
            getToken() {
                if (!client.account()) {
                    return Promise.reject(new Error('No user is logged in.'));
                }

                return client.token()
            },
            getUserRoles() {
                const user = account();
                if (!user || !user.idToken) return [];
                
                try {
                    // Parse the idToken if it's a string
                    const idTokenData = typeof user.idToken === 'string' 
                        ? JSON.parse(atob(user.idToken.split('.')[1]))
                        : user.idToken;
                    
                    return idTokenData?.roles || [];
                } catch (error) {
                    console.error('Error parsing idToken:', error);
                    return [];
                }
            }
        },
        theme: {
            useTheme: useTheme,
            getTheme: () => context.readState(s => s.theme || 'light'),
            setTheme(newTheme) {
                theme = newTheme;
                context.dispatch((state) => ({
                    ...state,
                    components: {
                        ...state.components,
                        theme: newTheme
                    }
                }));
            },
            changeTheme(cb) {
                events.push(cb);
                for (const event of events) {
                    event();
                }
            },
            setMode(mode) {
                context.dispatch((state) => ({
                    ...state,
                    theme: mode
                }));
                events.forEach(event => event());
            }
        },
        featureFlag: {
            useFeatureBoolean: useFliptBoolean,
            useFeatureVariant: useFliptVariant,
            useFeatureSelector: useFliptSelector,
        },
        analytics: {
            trackEvent(eventName, eventParams) {
                if (typeof window['gtag'] === 'function') {
                    window['gtag']('event', eventName, eventParams);
                } else {
                    console.warn('gtag is not defined');
                }
            }
        }
    });
}
